import { motion } from 'motion/react'
import type { StandingPillar, StandingResponse } from '../../api/types'
import { TOKENS } from '../ui/dashboard-primitives'

const CREAM = TOKENS.cream
const AMBER = TOKENS.amber
const TERRA = TOKENS.terracotta

const PILLAR_LABEL: Record<StandingPillar['key'], string> = {
  strength: 'Strength',
  consistency: 'Consistency',
  recovery: 'Recovery',
}
const PILLAR_ORDER: StandingPillar['key'][] = ['strength', 'consistency', 'recovery']

// Terracotta flags a genuinely weak pillar (below the halfway mark), never a
// merely-downward tick — a 95 that dipped from 97 is still excellent.
const LOW_PILLAR = 50

// What stands in for a number nobody measured. Never a 0: a zero is a reading,
// and reads as catastrophic where the truth is "not yet". Every app in the
// competitive pass does the same — WHOOP shows `--`, Withings a pair of rules.
const NO_VALUE = '\u2013'

function short(p: StandingPillar): boolean {
  return !!p.coverage && p.coverage.baseline_days < p.coverage.baseline_days_needed
}

function verdict(s: StandingResponse): string {
  if (s.score === null || s.score_prior === null || s.delta === null) {
    return 'Not enough to read yet. It fills in as sessions and health data arrive.'
  }
  if (s.delta === 0) return `Holding steady. Seven days ago it read ${s.score_prior}.`
  const mover = [...s.pillars]
    .filter((p) => p.delta !== null)
    .sort((a, b) => Math.abs(b.delta as number) - Math.abs(a.delta as number))[0]
  const name = mover ? PILLAR_LABEL[mover.key] : 'one pillar'
  return s.delta < 0
    ? `Most of that move came out of ${name.toLowerCase()}. Seven days ago Standing read ${s.score_prior}.`
    : `${name} carried most of that gain. Seven days ago it read ${s.score_prior}.`
}

/**
 * The Standing score read as three measured lines. Horizontal rules rather than
 * columns keep the same vocabulary as the page's hairlines and leave the score
 * the only loud figure (decisions D2/D3). The pillar that moved carries its
 * causes beneath it.
 */
export function StandingLedger({ standing }: { standing: StandingResponse }) {
  const byKey = Object.fromEntries(standing.pillars.map((p) => [p.key, p])) as Record<
    StandingPillar['key'],
    StandingPillar
  >
  const down = (standing.delta ?? 0) < 0
  const arrow = !standing.delta ? '' : down ? '▼' : '▲'
  const deltaColor = !standing.delta ? TOKENS.creamDim : down ? TERRA : AMBER
  const recovery = byKey.recovery

  return (
    <div>
      <span
        className="block text-[0.625rem] tracking-[0.28em] uppercase"
        style={{ fontFamily: 'var(--font-mono)', color: TOKENS.creamDim }}
      >
        Standing
      </span>

      <div className="flex items-baseline flex-wrap gap-x-[22px] gap-y-2 mt-1.5">
        <span
          className="tabular-nums"
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 500,
            fontSize: 'clamp(3.75rem, 8vw, 6rem)',
            lineHeight: 0.86,
            letterSpacing: '-0.03em',
            color: AMBER,
          }}
        >
          {standing.score ?? NO_VALUE}
          {/* No denominator on a non-value: "—/100" claims a scale nothing
              was measured against. */}
          {standing.score !== null && (
            <span style={{ fontSize: '0.3em', color: TOKENS.creamFaint }}>/100</span>
          )}
        </span>
        {standing.delta !== null && standing.delta !== 0 && (
          <span
            className="inline-flex items-center gap-[7px] text-[0.6875rem] tracking-[0.18em] uppercase"
            style={{ fontFamily: 'var(--font-mono)', color: deltaColor }}
          >
            <span style={{ fontSize: '0.8em' }}>{arrow}</span>
            <span>{Math.abs(standing.delta)} in seven days</span>
          </span>
        )}
      </div>

      <p
        className="mt-5 leading-[1.6]"
        style={{
          fontFamily: 'var(--font-body)',
          fontSize: '1.0625rem',
          color: TOKENS.creamSoft,
          maxWidth: '52ch',
        }}
      >
        {verdict(standing)}
      </p>

      {/* Ledger: three pillars across, peers. */}
      <div className="mt-9" style={{ maxWidth: 620 }}>
        <div
          className="grid grid-cols-3 gap-x-[26px] pt-[22px]"
          style={{ borderTop: `1px solid ${TOKENS.creamBorder}` }}
        >
          {PILLAR_ORDER.map((key) => {
            const p = byKey[key]
            if (!p) return null
            const missing = p.value === null
            const warn = !missing && (p.value as number) < LOW_PILLAR
            return (
              <div key={key} className="flex flex-col gap-[9px]">
                <span
                  className="text-[0.5875rem] tracking-[0.24em] uppercase"
                  style={{ fontFamily: 'var(--font-mono)', color: TOKENS.creamDim }}
                >
                  {PILLAR_LABEL[key]}
                </span>
                <span
                  className="tabular-nums leading-none"
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontWeight: 500,
                    fontSize: '2.375rem',
                    letterSpacing: '-0.03em',
                    // Faint, not terracotta: an unmeasured pillar is not a
                    // warning about the user. Terracotta would say they are
                    // doing badly at something nobody has measured.
                    color: missing ? TOKENS.creamFaint : warn ? TERRA : CREAM,
                  }}
                >
                  {p.value ?? NO_VALUE}
                </span>
                <span
                  className="text-[0.5875rem] tracking-[0.14em] uppercase"
                  style={{
                    fontFamily: 'var(--font-mono)',
                    color: warn ? TERRA : TOKENS.creamFaint,
                  }}
                >
                  {/* The slot that would hold the move holds the shortfall
                      instead: there is no move to report, and a fraction is
                      what every app in the competitive pass shows in its
                      place. Same size, same weight — hierarchy by colour, so
                      it never competes with the three numerals. */}
                  {missing
                    ? p.coverage
                      ? `${p.coverage.baseline_days} of ${p.coverage.baseline_days_needed} days`
                      : 'Not measured'
                    : p.delta === 0
                      ? 'Unchanged'
                      : `Was ${p.value_prior}`}
                </span>
              </div>
            )
          })}
        </div>

        {recovery && recovery.value !== null && short(recovery) && recovery.coverage && (
          <motion.p
            initial={{ opacity: 0, x: -6 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mt-6 pt-[18px] grid items-baseline"
            style={{
              borderTop: '1px solid ' + TOKENS.creamHairline,
              gridTemplateColumns: 'minmax(0, auto) 1fr',
              columnGap: 18,
            }}
          >
            {/* Amber, not terracotta. This is a limit of the measurement, not
                a warning about the person — the same reason the dash above is
                faint. Terracotta here would read as "your recovery is bad"
                when the honest claim is "we cannot see enough of it yet". */}
            <span
              className="text-[0.6875rem] tracking-[0.1em] uppercase whitespace-nowrap"
              style={{ fontFamily: 'var(--font-mono)', color: AMBER }}
            >
              History
            </span>
            <span className="text-sm" style={{ color: TOKENS.creamSoft }}>
              {`Recovery is reading ${recovery.coverage.baseline_days} of the ${recovery.coverage.baseline_days_needed} days it needs. Sleep and HRV sit out until then.`}
            </span>
          </motion.p>
        )}

        {standing.causes.length > 0 && (
          <ul
            className="mt-6 pt-[18px] list-none"
            style={{ borderTop: '1px solid ' + TOKENS.creamHairline }}
          >
            {standing.causes.slice(0, 3).map((c, i) => (
              <motion.li
                key={`${c.label}-${i}`}
                initial={{ opacity: 0, x: -6 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 + i * 0.08 }}
                className="grid items-baseline py-1.5"
                style={{ gridTemplateColumns: 'minmax(0, auto) 1fr', columnGap: 18 }}
              >
                <span
                  className="text-[0.6875rem] tracking-[0.1em] uppercase whitespace-nowrap"
                  style={{ fontFamily: 'var(--font-mono)', color: TERRA }}
                >
                  {c.label}
                </span>
                <span className="text-sm" style={{ color: TOKENS.creamSoft }}>
                  {c.detail}
                </span>
              </motion.li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
